/**
 * ONE pending-changes request per island, lane and access (VC-core,
 * 2026-10-18): the chip, the overlay status and the versions page ask the
 * same question of the same island, and every page with a chip paid each
 * of them. Answers are shared while in flight and kept once answered; a
 * refused or failed one is never kept.
 *
 * A request for a named lane first joins the island's default-lane answer
 * (the lane the server picks: my draft, or main) and uses it when that
 * answer names the same lane, so the chip (default) and the versions page
 * (its working lane) are one request.
 *
 * Kept until:
 *  - the reader's access changes (the review-access epoch);
 *  - the island changes: its op frames and its draft or commit announces
 *    (the dag-sync client calls `pendingChangesChanged` BEFORE its
 *    listeners reload), and any versioning write this client sends for it.
 */
import { reviewAccessEpoch } from "./review-access";

type Entry<T> = { scope: string; answer: Promise<T> };

const entries = new Map<string, Entry<unknown>>();
const generations = new Map<string, number>();

const scopeOf = (island: string) => `${reviewAccessEpoch()}:${generations.get(island) ?? 0}`;
const keyOf = (island: string, branch?: string) => `${island}\u0000${branch ?? ""}`;

function current<T>(island: string, branch: string | undefined, scope: string): Promise<T> | null {
  const e = entries.get(keyOf(island, branch));
  return e && e.scope === scope ? (e.answer as Promise<T>) : null;
}

function keep<T>(island: string, branch: string | undefined, scope: string, answer: Promise<T>): Promise<T> {
  const key = keyOf(island, branch);
  entries.set(key, { scope, answer });
  // a failure is never kept: the next ask tries again
  answer.catch(() => { if (entries.get(key)?.answer === answer) entries.delete(key); });
  return answer;
}

export function sharedPendingChanges<T extends { branch: string }>(
  island: string,
  branch: string | undefined,
  fetch: (branch?: string) => Promise<T>,
): Promise<T> {
  const scope = scopeOf(island);
  const held = current<T>(island, branch, scope);
  if (held) return held;
  const byDefault = current<T>(island, undefined, scope) ?? keep(island, undefined, scope, fetch());
  if (branch === undefined) return byDefault;
  return keep(island, branch, scope, byDefault.then(
    (a) => (a.branch === branch ? a : fetch(branch)),
    () => fetch(branch),
  ));
}

/** The island changed (its ops, its draft or commits, a write of mine):
 *  what was answered for it is asked again. */
export function pendingChangesChanged(island: string): void {
  generations.set(island, (generations.get(island) ?? 0) + 1);
  const prefix = `${island}\u0000`;
  for (const key of Array.from(entries.keys())) if (key.startsWith(prefix)) entries.delete(key);
}

export function resetPendingChangesForTests(): void {
  entries.clear();
  generations.clear();
}
